/**
 * Generic Swiper Carousel Component
 * Handles various carousel configurations with Swiper
 * Supports navigation, pagination, autoplay, breakpoints, and skeleton loaders
 */
export class SwiperCarousel extends HTMLElement {
  swiper = null

  connectedCallback() {
    // Wait for Swiper to be available
    if (!window.Swiper) {
      setTimeout(() => this.connectedCallback(), 100)
      return
    }

    // Initialize Swiper
    this.initSwiper()
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
  }

  initSwiper() {
    console.log('🔍 [Swiper] initSwiper')
    let container = this.querySelector('.swiper-carousel-container')
    if (!container) {
      // Try to find swiper-wrapper parent as fallback
      const wrapper = this.querySelector('.swiper-wrapper')
      if (wrapper && wrapper.parentElement) {
        container = wrapper.parentElement
        if (!container.classList.contains('swiper')) {
          container.classList.add('swiper', 'swiper-carousel-container')
        }
      } else {
        this.hideSkeleton()
        return
      }
    }

    const Swiper = window.Swiper
    const SwiperNavigation = window.SwiperNavigation
    const Pagination = window.Pagination
    const Autoplay = window.Autoplay

    // Get configuration from data attributes
    const modulesStr = this.dataset.modules || ''
    const loop = this.dataset.loop === 'true'
    const autoplayDelay = parseInt(this.dataset.autoplayDelay || '0', 10)
    const speed = parseInt(this.dataset.speed || '300', 10)
    const slidesPerView = this.dataset.slidesPerView
      ? parseFloat(this.dataset.slidesPerView)
      : 1
    const spaceBetween = this.dataset.spaceBetween
      ? parseInt(this.dataset.spaceBetween, 10)
      : 0
    const centeredSlides = this.dataset.centeredSlides === 'true'

    // Get navigation elements
    const nextButton = this.querySelector('[data-swiper-next]')
    const prevButton = this.querySelector('[data-swiper-prev]')
    const paginationEl = this.querySelector('[data-swiper-pagination]')
    console.log('🔍 [Swiper] Navigation buttons search:', {
      nextButton: nextButton,
      prevButton: prevButton,
    })

    // Capture reference to web component for use in callbacks
    const component = this

    // Wait a frame to ensure DOM is ready
    requestAnimationFrame(() => {
      try {
        // Build modules array
        const modules = []
        if (modulesStr.includes('navigation') && SwiperNavigation) {
          modules.push(SwiperNavigation)
        }
        if (modulesStr.includes('pagination') && Pagination) {
          modules.push(Pagination)
        }
        if (modulesStr.includes('autoplay') && Autoplay && autoplayDelay > 0) {
          modules.push(Autoplay)
        }

        // Parse breakpoints from JSON
        let breakpoints = null
        if (this.dataset.breakpoints) {
          try {
            breakpoints = JSON.parse(this.dataset.breakpoints)
          } catch (e) {
            console.warn('Invalid breakpoints JSON:', e)
          }
        }

        console.log('🔍 [Swiper] Navigation buttons search:', {
          nextButton: nextButton,
          prevButton: prevButton,
        })

        const config = {
          modules: modules,
          speed: speed,
          direction: 'horizontal',
          effect: 'slide',
          slidesPerView: slidesPerView,
          spaceBetween: spaceBetween,
          loop: loop,
          centeredSlides: centeredSlides,
          cssMode: false,
          allowTouchMove: true,
          watchOverflow: false,
          resistance: true,
          resistanceRatio: 0.85,
          followFinger: true,
          threshold: 5,
          navigation:
            modulesStr.includes('navigation') && nextButton && prevButton
              ? {
                  nextEl: nextButton,
                  prevEl: prevButton,
                }
              : false,
          pagination:
            modulesStr.includes('pagination') && paginationEl
              ? {
                  el: paginationEl,
                  clickable: this.dataset.paginationClickable !== 'false',
                }
              : false,
          autoplay:
            modulesStr.includes('autoplay') && autoplayDelay > 0
              ? {
                  delay: autoplayDelay,
                  disableOnInteraction: false,
                }
              : false,
          breakpoints: breakpoints,
          on: {
            init: function () {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                // Remove x-cloak from all slides after Swiper is initialized
                const slides = component.querySelectorAll(
                  '.swiper-slide[x-cloak]'
                )
                slides.forEach((slide) => {
                  slide.removeAttribute('x-cloak')
                })

                // Also remove x-cloak from navigation buttons and pagination
                const navButtons = component.querySelectorAll(
                  '[data-swiper-prev][x-cloak], [data-swiper-next][x-cloak]'
                )
                navButtons.forEach((btn) => {
                  btn.removeAttribute('x-cloak')
                })

                const pagination = component.querySelectorAll(
                  '[data-swiper-pagination][x-cloak]'
                )
                pagination.forEach((el) => {
                  el.removeAttribute('x-cloak')
                })

                // Hide skeleton
                component.hideSkeleton()

                // Show products wrapper if it exists
                const productsWrapper =
                  component.querySelector('ul.swiper-wrapper')
                if (productsWrapper) {
                  productsWrapper.style.opacity = '1'
                  productsWrapper.style.transition = 'opacity 0.3s ease'
                }
              })
            },
            slideChange: function () {
              // Ensure navigation buttons remain enabled after slide changes
              if (loop) {
                const nextBtn = this.navigation?.nextEl
                const prevBtn = this.navigation?.prevEl
                if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
                if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
              }
            },
            reachEnd: function () {
              // When loop is enabled, buttons should always work
              if (loop) {
                const nextBtn = this.navigation?.nextEl
                const prevBtn = this.navigation?.prevEl
                if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
                if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
              }
            },
            reachBeginning: function () {
              // When loop is enabled, buttons should always work
              if (loop) {
                const nextBtn = this.navigation?.nextEl
                const prevBtn = this.navigation?.prevEl
                if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
                if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
              }
            },
          },
        }

        component.swiper = new Swiper(container, config)
      } catch (error) {
        console.error('Error initializing Swiper carousel:', error)
        // Hide skeleton even if there's an error
        this.hideSkeleton()
      }
    })
  }

  hideSkeleton() {
    const skeleton = this.querySelector('[data-swiper-skeleton]')
    if (skeleton) {
      skeleton.style.opacity = '0'
      skeleton.style.pointerEvents = 'none'
      // Remove skeleton from DOM after transition
      setTimeout(() => {
        if (skeleton.parentNode) {
          skeleton.remove()
        }
      }, 300)
    }
  }
}
